import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { Input, Label } from "reactstrap";
import { useLoader } from "../../_helper/LoaderContext";
import {
    createEquipment,
    updateEquipment,
    getGlobalEquipmentList,
    importGlobalEquipment,
    EQUIPMENT_CATEGORIES,
} from "./service/equipment-service";


const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: "none" }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "44px",
        height: "44px",
        overflow: "hidden",
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "44px",
        padding: "0 8px",
        overflowX: "auto",
        overflowY: "hidden",
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: "#1b255b",
        margin: "2px",
        flexShrink: 0,
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: "#fff",
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: "#d32f2f",
        ":hover": {
            backgroundColor: "#ffcdd2",
            color: "white",
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
    }),
};

const initialForm = {
    name: "",
    code: "",
    description: "",
    category: "",
};

const normalizeCategory = (value) => {
    if (Array.isArray(value)) return value[0] || "";
    if (typeof value === "string") return value;
    return "";
};

export default function AddEquipment({ handleClose, data, getList }) {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [source, setSource] = useState("manual");
    const [selectedGlobalEquipment, setSelectedGlobalEquipment] = useState([]);
    const [equipmentMaster, setEquipmentMaster] = useState([]);
    const [equipmentSearch, setEquipmentSearch] = useState("");
    const [optionsLoading, setOptionsLoading] = useState(false);
    const requestSeq = useRef(0);

    useEffect(() => {
        if (data) {
            setForm({
                name: data.name || "",
                // code: data.code || "",
                description: data.description || "",
                category: normalizeCategory(data.category),
            });
        } else {
            setForm(initialForm);
        }
        setErrors({});
        setSource("manual");
        setSelectedGlobalEquipment([]);
        setEquipmentSearch("");
        setEquipmentMaster([]);
    }, [data]);

    useEffect(() => {
        if (data || source !== "import") return undefined;

        setOptionsLoading(true);
        const currentRequest = ++requestSeq.current;
        const timer = setTimeout(async () => {
            setOptionsLoading(true);
            try {
                const res = await getGlobalEquipmentList(equipmentSearch.trim());
                if (currentRequest !== requestSeq.current) return;
                if (res.data?.success) {
                    setEquipmentMaster(res.data.data || []);
                } else {
                    setEquipmentMaster([]);
                }
            } catch (error) {
                if (currentRequest === requestSeq.current) {
                    console.log(error);
                    setEquipmentMaster([]);
                }
            } finally {
                if (currentRequest === requestSeq.current) {
                    setOptionsLoading(false);
                }
            }
        }, 400);

        return () => {
            clearTimeout(timer);
            requestSeq.current += 1;
        };
    }, [equipmentSearch, source, data]);

    const equipmentOptions = useMemo(() => {
        const options = equipmentMaster.map((item) => ({
            label: item.name,
            value: item._id,
            category: item.category,
            description: item.description || "",
        }));
        return options.length > 0
            ? [{ label: t("Select All"), value: "*" }, ...options]
            : [];
    }, [equipmentMaster, t]);

    const handleGlobalEquipmentChange = (selected) => {
        if (!selected) {
            setSelectedGlobalEquipment([]);
            return;
        }

        const hasSelectAll = selected.some((opt) => opt.value === "*");
        const currentOptions = equipmentMaster.map((item) => ({
            label: item.name,
            value: item._id,
        }));

        if (hasSelectAll) {
            const allCurrentSelected =
                currentOptions.length > 0 &&
                currentOptions.every((opt) =>
                    selectedGlobalEquipment.some((sel) => sel.value === opt.value)
                );

            if (allCurrentSelected) {
                setSelectedGlobalEquipment((prev) =>
                    prev.filter(
                        (sel) => !currentOptions.some((opt) => opt.value === sel.value)
                    )
                );
            } else {
                setSelectedGlobalEquipment((prev) => {
                    const merged = [...prev];
                    currentOptions.forEach((opt) => {
                        if (!merged.some((sel) => sel.value === opt.value)) {
                            merged.push(opt);
                        }
                    });
                    return merged;
                });
            }
            return;
        }

        setSelectedGlobalEquipment(selected.filter((opt) => opt.value !== "*"));
    };

    const handleEquipmentSearch = (inputValue, meta) => {
        if (meta.action === "input-change") {
            setEquipmentSearch(inputValue);
        }
    };

    const handleImportSelected = async () => {
        const equipmentIds = selectedGlobalEquipment
            .filter((item) => item.value !== "*")
            .map((item) => item.value);

        if (equipmentIds.length === 0) {
            toast.info(t("No equipment selected"));
            return;
        }

        setLoading(true);
        try {
            const res = await importGlobalEquipment(equipmentIds);
            if (res.data.success === true) {
                toast.success(res.data.message);
                setSelectedGlobalEquipment([]);
                await getList();
                handleClose();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = t("Please enter equipment name");
        // if (!form.code.trim()) next.code = t("Please enter equipment code");
        if (!form.category) next.category = t("Please select category");
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const formData = new FormData();
        formData.append("name", form.name.trim());
        // formData.append("code", form.code.trim());
        formData.append("description", form.description.trim());
        formData.append("category", form.category);

        setLoading(true);
        try {
            const res = data
                ? await updateEquipment(data._id, formData)
                : await createEquipment(formData);

            if (res.data.success === true) {
                toast.success(res.data.message);
                getList();
                handleClose();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal.Body>
                {!data && (
                    <div className="d-flex align-items-center tw-space-x-4 mb-3">
                        <div className="radio radio-primary">
                            <Input
                                id="equipment-radio-manual"
                                type="radio"
                                name="equipmentSource"
                                value="manual"
                                checked={source === "manual"}
                                onChange={() => setSource("manual")}
                            />
                            <Label for="equipment-radio-manual" className="mb-0 pe-2">
                                {t("Manual")}
                            </Label>
                        </div>
                        <div className="radio radio-primary">
                            <Input
                                id="equipment-radio-import"
                                type="radio"
                                name="equipmentSource"
                                value="import"
                                checked={source === "import"}
                                onChange={() => setSource("import")}
                            />
                            <Label for="equipment-radio-import" className="mb-0 pe-2">
                                {t("Import From Database")}
                            </Label>
                        </div>
                        {source === "import" && selectedGlobalEquipment.length > 0 && (
                            <Button onClick={handleImportSelected}>
                                {t("Import")} ({selectedGlobalEquipment.length})
                            </Button>
                        )}
                    </div>
                )}
                {source === "import" && !data ? (
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        options={equipmentOptions}
                        value={selectedGlobalEquipment}
                        onChange={handleGlobalEquipmentChange}
                        onInputChange={handleEquipmentSearch}
                        filterOption={() => true}
                        isLoading={optionsLoading}
                        placeholder={t("Add equipment from global list...")}
                        components={{ Option }}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                        isClearable
                        noOptionsMessage={() =>
                            optionsLoading ? t("Loading...") : t("No equipment found")
                        }
                    />
                ) : (
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <Form.Group className="mb-3" controlId="equipmentName">
                        <Form.Label>
                            {t("Equipment Name")}
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t("Enter equipment name")}
                            value={form.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="equipmentCategory">
                        <Form.Label>
                            {t("Category")}
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            value={form.category}
                            onChange={(e) => handleChange("category", e.target.value)}
                            isInvalid={!!errors.category}
                        >
                            <option value="">{t("Select category")}</option>
                            {EQUIPMENT_CATEGORIES.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.category}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-0" controlId="equipmentDescription">
                        <Form.Label>{t("Description")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder={t("Enter description")}
                            value={form.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </Form.Group>
                </Form>
                )}
            </Modal.Body>
            {source === "manual" || data ? (
                <Modal.Footer>
                    <Button variant="primary" onClick={handleSubmit}>
                        {data ? t("Update") : t("Create")}
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Close")}
                    </Button>
                </Modal.Footer>
            ) : null}
        </>
    );
}
